import { useCallback, useMemo } from 'react';
import { FilterOptions } from '../../../utils/productListFilters';
import {
    CusProductListFilterBullet,
    CusProductListUseFilterBulletsOptions,
    CusProductListUseFilterBulletsResult
} from '../cus-product-list.data';

const buildBullets = (filters: FilterOptions, currency: string, categoryNames: Record<string, string>): CusProductListFilterBullet[] => {
    const bullets: CusProductListFilterBullet[] = [];

    if (filters.priceRange) {
        bullets.push({
            key: 'price',
            label: `${filters.priceRange.min} ${currency} - ${filters.priceRange.max} ${currency}`
        });
    }

    if (filters.categoryIds) {
        for (const id of filters.categoryIds) {
            bullets.push({ key: `category-${id}`, label: categoryNames[id] ?? id });
        }
    }

    if (filters.sizes) {
        for (const size of filters.sizes) {
            bullets.push({ key: `size-${size}`, label: size });
        }
    }

    if (filters.colors) {
        for (const color of filters.colors) {
            bullets.push({ key: `color-${color}`, label: color });
        }
    }

    return bullets;
};

const removeFilter = (current: FilterOptions, bulletKey: string): FilterOptions => {
    const next = { ...current };

    if (bulletKey === 'price') {
        delete next.priceRange;
        return next;
    }

    if (bulletKey.startsWith('category-')) {
        const id = bulletKey.replace('category-', '');
        next.categoryIds = current.categoryIds?.filter(c => c !== id);
        if (!next.categoryIds?.length) {
            delete next.categoryIds;
        }
        return next;
    }

    if (bulletKey.startsWith('size-')) {
        const size = bulletKey.replace('size-', '');
        next.sizes = current.sizes?.filter(s => s !== size);
        if (!next.sizes?.length) {
            delete next.sizes;
        }
        return next;
    }

    if (bulletKey.startsWith('color-')) {
        const color = bulletKey.replace('color-', '');
        next.colors = current.colors?.filter(c => c !== color);
        if (!next.colors?.length) {
            delete next.colors;
        }
        return next;
    }

    return next;
};

export default function useFilterBullets({
    activeFilters,
    currency,
    categoryNames,
    onRemove
}: CusProductListUseFilterBulletsOptions): CusProductListUseFilterBulletsResult {
    const bullets = useMemo(() => buildBullets(activeFilters, currency, categoryNames), [activeFilters, currency, categoryNames]);

    const handleRemove = useCallback(
        (bulletKey: string) => {
            onRemove(removeFilter(activeFilters, bulletKey));
        },
        [activeFilters, onRemove]
    );

    return { bullets, handleRemove };
}
